// @ts-nocheck
/**
 * Scroll Depth Tracking
 *
 * Tracks when users scroll to 25%, 50%, 75%, and 100% of page content.
 * Each milestone is only tracked once per page view.
 */

const MILESTONES = [25, 50, 75, 100];

export class ScrollDepthTracker {
  constructor(analytics) {
    this.analytics = analytics;
    this.trackedMilestones = new Set();
    this.isTracking = false;
    this.handleScroll = this.handleScroll.bind(this);
  }

  /**
   * Start tracking scroll depth on current page
   */
  start() {
    if (this.isTracking || typeof window === 'undefined') {
      return;
    }

    // Reset tracked milestones for new page
    this.trackedMilestones.clear();

    // Add scroll listener with passive flag for performance
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.isTracking = true;

    // Check initial scroll position (in case user refreshed mid-page)
    this.checkScrollDepth();
  }

  /**
   * Stop tracking (called when leaving page)
   */
  stop() {
    if (!this.isTracking || typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('scroll', this.handleScroll);
    this.isTracking = false;
  }

  /**
   * Handle scroll events (throttled via passive listener)
   */
  handleScroll() {
    // Use requestAnimationFrame for smooth performance
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.checkScrollDepth();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  /**
   * Calculate current scroll depth and track milestones
   */
  checkScrollDepth() {
    if (typeof window === 'undefined') {
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;

    // Calculate percentage scrolled
    const scrollableHeight = documentHeight - windowHeight;
    if (scrollableHeight <= 0) {
      // Page is too short to scroll
      return;
    }

    const scrollPercentage = Math.round((scrollTop / scrollableHeight) * 100);

    // Check each milestone
    for (const milestone of MILESTONES) {
      if (
        scrollPercentage >= milestone &&
        !this.trackedMilestones.has(milestone)
      ) {
        this.trackMilestone(milestone);
      }
    }
  }

  /**
   * Track a scroll depth milestone
   */
  trackMilestone(depth) {
    // Mark as tracked to prevent duplicates
    this.trackedMilestones.add(depth);

    // Send analytics event
    if (this.analytics) {
      this.analytics.trackEvent('scroll.depth', {
        depth,
        page: window.location.pathname,
      });
    }
  }

  /**
   * Reset tracking for new page view
   */
  reset() {
    this.stop();
    this.start();
  }
}
